import { useState, useEffect } from 'react';
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Youtube, Twitter, Globe } from 'lucide-react';
import { useScrollToSection } from '../hooks/useScrollToSection';
import { config } from '../config/config';
import { LegalDialogs } from './LegalDialogs';

export function Footer() {
  const { scrollToSection } = useScrollToSection();
  const currentYear = new Date().getFullYear();
  const [socialLinks, setSocialLinks] = useState<any[]>([]);

  useEffect(() => {
    fetch('/api/social.php')
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.social) setSocialLinks(data.social);
      })
      .catch(() => {});
  }, []);

  const IconMap: Record<string, any> = {
    Facebook,
    Instagram,
    Linkedin,
    Youtube,
    Twitter,
    Globe
  };

  return (
    <footer className="bg-slate-950 text-slate-300 relative overflow-hidden">
      <div className="absolute inset-0 opacity-40">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(148,163,184,0.12),transparent_28%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,23,42,0.2),rgba(15,23,42,0.9))]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
          <div className="lg:col-span-2">
            <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-left group">
              <div className="relative overflow-hidden h-14 md:h-24 w-44 md:w-72 flex items-center justify-start">
                <img 
                  src="/logo.png" 
                  alt="Free Zone Logistics Park" 
                  className="absolute left-[-5%] h-[150%] w-auto max-w-none object-contain transition-all duration-300 group-hover:scale-105 brightness-0 invert" 
                />
              </div>
            </button>

            <div className="space-y-3">
              <a href={`tel:${config.contact.phone.replace(/\s/g, '')}`} className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors group">
                <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-primary transition-colors">
                  <Phone className="h-4 w-4" />
                </span>
                <span className="text-sm">{config.contact.phone}</span>
              </a>
              <a href={`mailto:${config.contact.email}`} className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors group">
                <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-primary transition-colors">
                  <Mail className="h-4 w-4" />
                </span>
                <span className="text-sm">{config.contact.email}</span>
              </a>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(config.contact.address)}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors group"
              >
                <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-primary transition-colors">
                  <MapPin className="h-4 w-4" />
                </span>
                <span className="text-sm">
                  {config.contact.address}
                </span>
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-white mb-4 font-semibold text-lg">Empresa</h3>
            <ul className="space-y-3">
              {[
                { label: 'Sobre Nosotros', id: 'about' },
                { label: 'Servicios', id: 'services' },
                { label: 'Ubicación', id: 'location' },
              ].map((link) => (
                <li key={link.id}>
                  <button onClick={() => scrollToSection(link.id)} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white mb-4 font-semibold text-lg">Legal</h3>
            <LegalDialogs />
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">© {currentYear} Free Zone Logistics Park. Todos los derechos reservados.</p>

          <div className="flex items-center gap-3">
            {socialLinks.map((social) => {
              const Icon = IconMap[social.icon] || MapPin;
              return (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                >
                  <Icon className="h-5 w-5" />
                </a>
              );
            })}
          </div>
        </div>
      </div>

    </footer>
  );
}
